import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, X } from 'lucide-react';
import { useDebounce } from '../../hooks/useDebounce';
import { mockProducts } from '../../data/mockData';

const SearchBar = ({ onSearch }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const debouncedQuery = useDebounce(query, 300);

  useEffect(() => {
    if (debouncedQuery.length >= 2) {
      // Simular búsqueda
      const filtered = mockProducts.filter(product =>
        product.title.toLowerCase().includes(debouncedQuery.toLowerCase()) ||
        product.series?.toLowerCase().includes(debouncedQuery.toLowerCase())
      ).slice(0, 8);
      setResults(filtered);
    } else {
      setResults([]);
    }
  }, [debouncedQuery]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        if (query === '') {
          setIsExpanded(false);
        }
        setResults([]);
      }
    };

    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        setIsExpanded(false);
        setQuery('');
        setResults([]);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [query]);

  const handleExpand = () => {
    setIsExpanded(true);
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  const handleClear = () => {
    setQuery('');
    setResults([]);
    inputRef.current?.focus();
  };

  const highlightText = (text, highlight) => {
    if (!highlight.trim()) return text;
    const parts = text.split(new RegExp(`(${highlight})`, 'gi'));
    return parts.map((part, i) => 
      part.toLowerCase() === highlight.toLowerCase() ? 
        <span key={i} className="text-primary font-semibold">{part}</span> : part
    );
  };

  return (
    <div ref={containerRef} className="relative">
      <motion.div
        initial={false}
        animate={{
          width: isExpanded ? 400 : 40
        }}
        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
        className="relative"
      >
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Buscar productos..."
          className={`w-full h-10 rounded-full border-2 transition-all ${
            isExpanded ? 'pl-11 pr-10 border-primary' : 'pl-10 pr-0 border-transparent'
          } focus:outline-none focus:border-primary bg-muted`}
        />
        
        <button
          onClick={isExpanded ? undefined : handleExpand}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-primary transition-colors"
        >
          <Search className="w-5 h-5" />
        </button>

        {isExpanded && query && (
          <button
            onClick={handleClear}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-destructive transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </motion.div>

      {/* Resultados de búsqueda */}
      <AnimatePresence>
        {isExpanded && results.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="absolute top-full left-0 right-0 mt-2 bg-background border border-border rounded-lg shadow-xl max-h-96 overflow-y-auto z-50"
          >
            <div className="p-2">
              {results.map((product, index) => (
                <a
                  key={product.id}
                  href={`#product-${product.id}`}
                  className={`flex items-center gap-3 p-3 rounded-lg hover:bg-muted transition-colors ${
                    index === selectedIndex ? 'bg-muted' : ''
                  }`}
                  onClick={() => {
                    setIsExpanded(false);
                    setQuery('');
                    setResults([]);
                  }}
                >
                  <img
                    src={product.image}
                    alt={product.title}
                    className="w-12 h-16 object-cover rounded"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium line-clamp-1">
                      {highlightText(product.title, query)}
                    </p>
                    <p className="text-xs text-muted-foreground">{product.category}</p>
                    <p className="text-sm font-semibold text-primary mt-1">
                      €{(product.discountPrice || product.price).toFixed(2)}
                    </p>
                  </div>
                </a>
              ))}
            </div>
            <div className="border-t border-border p-3">
              <button className="text-sm text-primary hover:underline font-medium">
                Ver todos los resultados ({results.length})
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SearchBar;